/**
 * src/services/spatial/mapProvider.ts
 *
 * Provider-agnostic map abstraction for the GeoLink spatial-objects
 * subsystem.
 *
 * GeoTrust currently ships with mapbox-gl-js wired directly into App.tsx.
 * The spatial-objects feature must work today on Mapbox AND remain
 * straightforward to switch over to MapTiler SDK JS or MapLibre GL JS
 * later. Because all three libraries share a near-identical low-level
 * API (MapTiler SDK and MapLibre GL are forks of mapbox-gl), this
 * abstraction is intentionally a thin pass-through facade. Each
 * provider wraps a real map instance and exposes only the operations
 * the spatial layers need. This:
 *
 *   1. Keeps the existing Mapbox map untouched (App.tsx still creates
 *      `mapboxgl.Map` directly — we just wrap it in a provider).
 *   2. Lets us swap providers behind `VITE_MAP_PROVIDER` without
 *      changing the spatial-layer components.
 *   3. Provides a single seam for future provider-specific quirks
 *      (e.g. MapTiler's planet rendering, MapLibre's terrain DEM).
 */

import type {
  Feature,
  FeatureCollection,
  Geometry,
  Position,
} from './geoLinkTypes';
import { getConfiguredProvider, type MapProviderId } from './spatialEnv';

/**
 * GeoJSON source data accepted by the abstraction. Matches the
 * `mapbox-gl` / `maplibre-gl` shape exactly so providers can pass
 * through without translation.
 */
export type ProviderGeoJsonData =
  | string
  | Feature
  | FeatureCollection
  | { type: 'Feature'; geometry: Geometry; properties?: Record<string, unknown> }
  | { type: 'FeatureCollection'; features: Feature[] };

export interface ProviderSourceSpec {
  type: 'geojson';
  data: ProviderGeoJsonData;
  /** Whether to compute feature ids when missing (mapbox/maplibre option). */
  generateId?: boolean;
  /** Tolerance for vector-tile-like simplification on the source. */
  tolerance?: number;
}

/**
 * Raster XYZ tile source spec — used to add complementary imagery
 * layers (e.g. MapTiler satellite tiles) on top of the existing
 * basemap. Mapbox, MapLibre and MapTiler SDK all accept this shape
 * via `addSource(id, {type: 'raster', tiles, tileSize, …})`.
 */
export interface ProviderRasterSourceSpec {
  type: 'raster';
  /** Fully-formed XYZ URLs with `{z}` `{x}` `{y}` placeholders. */
  tiles: string[];
  /** Tile size in pixels (256 for most XYZ tilesets, 512 for retina). */
  tileSize?: number;
  /** Per-tile attribution shown in the map's attribution control. */
  attribution?: string;
  /** Min/max zoom — keeps the GPU from fetching tiles outside the source's coverage. */
  minzoom?: number;
  maxzoom?: number;
}

/**
 * Subset of the mapbox/maplibre/maptiler layer specification used by
 * the spatial layers. We don't need to model every layer type here;
 * the spatial subsystem only emits fill, line, fill-extrusion, symbol,
 * and circle layers, all of which share the same paint/layout shape
 * across the three libraries.
 */
export type ProviderLayerType =
  | 'fill'
  | 'line'
  | 'fill-extrusion'
  | 'symbol'
  | 'circle'
  | 'raster';

export interface ProviderLayerSpec {
  id: string;
  type: ProviderLayerType;
  source: string;
  filter?: unknown[];
  layout?: Record<string, unknown>;
  paint?: Record<string, unknown>;
  minzoom?: number;
  maxzoom?: number;
}

/** Map bounds the way GeoLink expects them: {lng,lat} corners. */
export interface ProviderBounds {
  sw: { lng: number; lat: number };
  ne: { lng: number; lat: number };
}

export type ProviderInteractiveEvent = 'click' | 'mouseenter' | 'mouseleave' | 'mousemove';

/** Minimal pointer-style event the providers emit to the spatial layers. */
export interface ProviderMapEvent {
  type: ProviderInteractiveEvent;
  point: { x: number; y: number };
  lngLat: { lng: number; lat: number };
  /**
   * Features under the pointer at the time of the event. Only populated
   * for layer-targeted handlers; global handlers receive an empty array
   * and the spatial layers re-query via `queryFeatures()`.
   */
  features: Array<{
    id?: string | number;
    properties: Record<string, unknown>;
    geometry: Geometry;
    layer?: { id: string };
  }>;
  /** Original native event so consumers can stop propagation if needed. */
  originalEvent?: Event;
}

export type ProviderEventHandler = (event: ProviderMapEvent) => void;

/**
 * Provider-agnostic map handle used by the spatial subsystem.
 *
 * Each provider implementation wraps an underlying map instance and
 * is responsible for:
 *   - matching layer/source IDs across calls,
 *   - debouncing duplicate addLayer/addSource attempts (the spatial
 *     layers call them inside React effects which can re-run),
 *   - cleaning up its own listeners on `dispose()`.
 *
 * The instance itself is *not* responsible for creating the map — that
 * happens in App.tsx (Mapbox today) or in the provider's `attach()`
 * helper for MapTiler/MapLibre when those become primary.
 */
export interface SpatialMapProvider {
  /** Stable provider identifier so the UI can show a "Provider: mapbox" pill. */
  readonly id: MapProviderId;
  /**
   * Human-readable name shown in the provider indicator UI control.
   * Distinct from `id` so we can render "MapTiler SDK" instead of
   * "maptiler".
   */
  readonly displayName: string;
  /**
   * The underlying map instance. Typed as `unknown` because consumers
   * should NOT depend on the concrete library — go through the provider
   * methods instead. Exposed only for advanced overlays (the existing
   * App.tsx code keeps using its `mapboxgl.Map` reference directly,
   * which is fine; we don't double-wrap it).
   */
  readonly nativeMap: unknown;

  isStyleLoaded(): boolean;
  whenReady(cb: () => void): void;

  hasLayer(id: string): boolean;
  hasSource(id: string): boolean;

  addSource(id: string, spec: ProviderSourceSpec): void;
  /** Add a raster XYZ tile source — used for the MapTiler overlay. */
  addRasterSource(id: string, spec: ProviderRasterSourceSpec): void;
  setSourceData(id: string, data: ProviderGeoJsonData): void;
  removeSource(id: string): void;

  addLayer(spec: ProviderLayerSpec, beforeId?: string): void;
  removeLayer(id: string): void;
  setFilter(layerId: string, filter: unknown[] | null): void;
  setLayerVisibility(layerId: string, visible: boolean): void;
  setPaintProperty(layerId: string, name: string, value: unknown): void;
  setLayoutProperty(layerId: string, name: string, value: unknown): void;

  on(event: ProviderInteractiveEvent, layerId: string, handler: ProviderEventHandler): void;
  off(event: ProviderInteractiveEvent, layerId: string, handler: ProviderEventHandler): void;

  /**
   * Returns features under the given pixel point that belong to one
   * of the supplied layer ids. Used by the interaction panel to find
   * the topmost spatial object at a click location.
   */
  queryFeaturesAtPoint(
    point: { x: number; y: number },
    layerIds: string[],
  ): Array<{ id?: string | number; properties: Record<string, unknown>; geometry: Geometry; layer?: { id: string } }>;

  getBounds(): ProviderBounds;
  getZoom(): number;
  getCenter(): { lng: number; lat: number };
  flyTo(opts: { center?: Position; zoom?: number; pitch?: number; bearing?: number; speed?: number }): void;

  /**
   * Detach all listeners we've added, but DO NOT destroy the
   * underlying map (App.tsx owns its lifecycle). Provider
   * implementations that own the map outright (the future
   * MapTiler/MapLibre primary case) override this to also remove
   * the map.
   */
  dispose(): void;
}

/**
 * Factory: returns the provider matching `VITE_MAP_PROVIDER` configured
 * in env. The `nativeMap` must be the already-constructed underlying
 * map instance — we never construct it ourselves from this factory
 * because the host app (App.tsx) owns the map lifecycle.
 *
 * For dynamic-loading providers (MapTiler / MapLibre) the import is
 * lazy so users on a Mapbox-only deployment don't have to install
 * the other SDKs.
 */
export async function createSpatialMapProvider(
  nativeMap: unknown,
  override?: MapProviderId,
): Promise<SpatialMapProvider> {
  const id = override || getConfiguredProvider();
  switch (id) {
    case 'mapbox': {
      const { createMapboxProvider } = await import('./mapboxProvider');
      return createMapboxProvider(nativeMap);
    }
    case 'maptiler': {
      const { createMapTilerProvider } = await import('./maptilerProvider');
      return createMapTilerProvider(nativeMap);
    }
    case 'maplibre': {
      const { createMapLibreProvider } = await import('./maplibreProvider');
      return createMapLibreProvider(nativeMap);
    }
    default: {
      const _exhaustive: never = id;
      throw new Error(`Unsupported map provider: ${_exhaustive}`);
    }
  }
}

/**
 * Synchronous variant for callers that already know the provider id at
 * compile-time (today: the Mapbox path used by App.tsx). Throws if you
 * ask for a provider whose impl module hasn't been loaded yet.
 */
export function createSpatialMapProviderSync(
  nativeMap: unknown,
  override?: MapProviderId,
): SpatialMapProvider {
  const id = override || getConfiguredProvider();
  if (id !== 'mapbox') {
    throw new Error(
      `[mapProvider] createSpatialMapProviderSync only supports "mapbox" today; ` +
      `requested "${id}". Use createSpatialMapProvider() for async providers.`,
    );
  }
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { createMapboxProvider } = require('./mapboxProvider') as typeof import('./mapboxProvider');
  return createMapboxProvider(nativeMap);
}
